/**
 * Date utility functions for converting between different date formats
 */

/**
 * Converts a date from YYYY-MM-DD format (HTML date input) to DD/MM/YYYY format (backend API)
 * @param dateString - Date string in YYYY-MM-DD format (e.g., "2024-01-25")
 * @returns Date string in DD/MM/YYYY format (e.g., "25/01/2024") or empty string if invalid
 */
export const convertToBackendDateFormat = (dateString: string): string => {
  if (!dateString) return "";
  
  const [year, month, day] = dateString.split("-");
  if (!year || !month || !day) return "";
  
  return `${day}/${month}/${year}`;
};

/**
 * Converts a date from DD/MM/YYYY format (backend API) to YYYY-MM-DD format (HTML date input)
 * @param dateString - Date string in DD/MM/YYYY format (e.g., "25/01/2024")
 * @returns Date string in YYYY-MM-DD format (e.g., "2024-01-25") or empty string if invalid
 */
export const convertFromBackendDateFormat = (dateString: string): string => {
  if (!dateString) return "";
  
  const [day, month, year] = dateString.split("/");
  if (!year || !month || !day) return "";
  
  return `${year}-${month}-${day}`;
};

/**
 * Converts a datetime string (Colombia time) to UTC ISO string for the API.
 * Interprets input as America/Bogota (UTC-5). Used by news (event_timestamp) and swimmers (registration_timestamp).
 * @param dateTimeString - "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss" (e.g. from form state)
 * @returns UTC ISO string (e.g. "2026-03-11T18:47:00.000Z")
 */
export const colombiaDateTimeToUTCISO = (dateTimeString: string): string => {
  const trimmed = dateTimeString.trim();
  const [datePart, timePart] = trimmed.split("T");
  if (!datePart || !timePart) return dateTimeString;
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  if (
    [year, month, day, hours, minutes].some((n) => Number.isNaN(n))
  ) return dateTimeString;
  const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(
    day,
  ).padStart(2, "0")}T${String(hours).padStart(2, "0")}:${String(
    minutes,
  ).padStart(2, "0")}:00`;
  const tempDate = new Date(`${dateStr}-05:00`);
  return tempDate.toISOString();
};
